import {
  Box,
  Button,
  TextInput,
  Select,
  Grid,
  Textarea,
  Checkbox,
  Text,
  Stack,
  Group,
  MultiSelect,
  Tooltip,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { yupResolver } from "mantine-form-yup-resolver";
import { Database } from "types/supabase";
import * as yup from "yup";
import {
  useCreateOrganizer,
  useUpdateOrganizer,
  useQueryOrganizerVenues,
} from "hooks/organizers";
import { useNavigate } from "react-router-dom";
import { EditPicture, TranslateButton } from "ui";
import { useState, useEffect } from "react";
import { useQueryVenues } from "hooks/venues";
import { useAppTranslation } from "hooks/useTranslation";
import { useQueryCurrentUser } from "hooks/users";
import { supabaseClient } from "supabase/supabase";
import { notifications } from "@mantine/notifications";
import { queryClient } from "utils";

interface OrganizerFormProps {
  isNew?: boolean;
  organizer?: Database["public"]["Tables"]["organizers"]["Row"];
  isApplication?: boolean;
  onCreated?: (organizer: { id: number; name: string }) => void;
}

export const OrganizerForm = (props: OrganizerFormProps) => {
  const { isNew = false, organizer, isApplication = false, onCreated } = props;
  const navigate = useNavigate();
  const [file, setFile] = useState<File>();
  const [selectedVenueIds, setSelectedVenueIds] = useState<number[]>([]);
  const [hasConnectedUser, setHasConnectedUser] = useState(false);
  const [connectedUserEmail, setConnectedUserEmail] = useState<string | null>(
    null
  );
  const [imageError, setImageError] = useState(false);
  const { t } = useAppTranslation();
  const { user: currentUser } = useQueryCurrentUser();

  // Check if the current user is an admin or an organizer editing their own profile
  const isAdmin = currentUser?.organizer_id === null;

  // Get all venues
  const { venues } = useQueryVenues({});

  // Get the venues associated with this organizer if editing
  const organizerVenues = useQueryOrganizerVenues(
    isNew ? undefined : organizer?.id
  );

  // Check if organizer has a connected user
  useEffect(() => {
    const checkConnectedUser = async () => {
      if (!isNew && organizer) {
        const { data } = await supabaseClient
          .from("users")
          .select("id, email")
          .eq("organizer_id", organizer.id)
          .single();
        setHasConnectedUser(!!data);
        setConnectedUserEmail(data?.email || null);
      }
    };
    checkConnectedUser();
  }, [isNew, organizer]);

  const updateOrganizer = useUpdateOrganizer(() => {
    // For admin users, navigate back to the previous page
    if (isAdmin) {
      navigate(-1);
    }
    // For organizers updating their own profile, stay on the page and show notification
    else {
      notifications.show({
        title: t("common.success"),
        message: t("organizers.profile_updated"),
        color: "green",
      });
    }
  });
  const createOrganizer = useCreateOrganizer((created) => {
    if (onCreated && created && created.id && created.name) {
      onCreated({ id: created.id, name: created.name });
    } else {
      navigate(-1);
    }
  });

  // When venues load, set the initial selected values
  useEffect(() => {
    if (!isNew && organizerVenues.data) {
      const venueIds = organizerVenues.data.map((venue) => venue.id!);
      setSelectedVenueIds(venueIds);
    }
  }, [organizerVenues.data, isNew]);

  const schema = yup.object().shape({
    name: yup.string().min(3, t("organizers.name_min_length")),
  });

  const form = useForm({
    initialValues: {
      name: organizer?.name || "",
      email: organizer?.email || "",
      phone: organizer?.phone || "",
      whatsapp: organizer?.whatsapp || "",
      preferredLanguage: organizer?.preferred_language || "",
      is_municipality: organizer?.is_municipality || false,
      enable_weekly: organizer?.enable_weekly || false,
      description: organizer?.description || "",
      description_pt: organizer?.description_pt || "",
      website: organizer?.website || "",
    },

    validate: yupResolver(schema),
  });

  const formSubmit = async (values: any) => {
    // Validate that both description fields are filled if one is filled
    if (
      (values.description && !values.description_pt) ||
      (!values.description && values.description_pt)
    ) {
      if (!values.description) {
        form.setFieldError(
          "description",
          t("organizers.both_descriptions_required")
        );
      }
      if (!values.description_pt) {
        form.setFieldError(
          "description_pt",
          t("organizers.both_descriptions_required")
        );
      }
      return;
    }

    // Make image optional instead of required
    if (imageError) {
      return;
    }

    const editedOrganizer = {
      name: values.name,
      email: values.email,
      phone: values.phone,
      whatsapp: values.whatsapp,
      preferred_language: values.preferredLanguage,
      is_municipality: values.is_municipality,
      enable_weekly: values.enable_weekly,
      description: values.description,
      description_pt: values.description_pt,
      website: values.website,
      currPicture: null,
      newPicture: file,
      venueIds: isAdmin ? selectedVenueIds : undefined,
    };

    if (isNew) {
      try {
        await createOrganizer.mutateAsync(editedOrganizer);
        // onCreated will be called by the mutation's onSuccess
      } catch (error: any) {
        if (error.code === "23505") {
          form.setErrors({ name: t("organizers.name_already_exists") });
        } else {
          form.setErrors(error.message);
        }
      }
    } else {
      await updateOrganizer.mutateAsync({
        id: organizer!.id,
        uid: organizer!.uid,

        ...editedOrganizer,
        currPicture: organizer!.picture,
      });
      // Clear the organizer query cache to ensure fresh data is fetched
      queryClient.invalidateQueries({
        queryKey: ["organizer", `id:${organizer?.id}`],
      });
    }
  };

  const handleResetPassword = async () => {
    if (!organizer || !connectedUserEmail) return;

    await supabaseClient.auth.resetPasswordForEmail(connectedUserEmail, {
      redirectTo: "https://org.pasemana.pt/reset-password",
    });
    notifications.show({
      title: t("common.success"),
      message: t("common.reset_password_sent"),
      color: "green",
    });
  };

  // Create venue data for MultiSelect
  const venueOptions =
    venues?.map((venue) => ({
      value: venue.id!.toString(),
      label: venue.name || "Unnamed venue",
    })) || [];

  return (
    <Box>
      <form onSubmit={form.onSubmit(formSubmit)}>
        <Grid>
          <Grid.Col span={6}>
            <TextInput
              label={t("organizers.name")}
              {...form.getInputProps("name")}
            />
          </Grid.Col>
          <Grid.Col span={6}></Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label={t("organizers.email")}
              {...form.getInputProps("email")}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label={t("organizers.website")}
              {...form.getInputProps("website")}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label={t("organizers.phone")}
              {...form.getInputProps("phone")}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label={t("organizers.whatsapp")}
              {...form.getInputProps("whatsapp")}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <Textarea
              label={t("organizers.description")}
              right="sm"
              maxRows={8}
              minRows={8}
              autosize
              {...form.getInputProps("description")}
              rightSection={
                <TranslateButton
                  originalText={form.values.description_pt}
                  tragetLang="en"
                  onTranslated={(translatedText) => {
                    form.setFieldValue("description", translatedText);
                  }}
                />
              }
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <Textarea
              label={t("organizers.description_pt")}
              maxRows={8}
              minRows={8}
              autosize
              {...form.getInputProps("description_pt")}
              rightSection={
                <TranslateButton
                  originalText={form.values.description}
                  tragetLang="pt"
                  onTranslated={(translatedText) => {
                    form.setFieldValue("description_pt", translatedText);
                  }}
                />
              }
            />
          </Grid.Col>

          {/* Only show venues selection for admins, unless isApplication is true */}
          {isAdmin && !isApplication && (
            <Grid.Col span={6}>
              <MultiSelect
                key={`venues-${selectedVenueIds.join(",")}`}
                label={t("organizers.venues")}
                mt="md"
                data={venueOptions}
                searchable
                value={selectedVenueIds.map((id) => id.toString())}
                onChange={(values) =>
                  setSelectedVenueIds(
                    values.map((value) => parseInt(value, 10))
                  )
                }
              />
            </Grid.Col>
          )}

          {/* Only show language and municipality for admins, unless isApplication is true */}
          {isAdmin && !isApplication && (
            <Grid.Col span={6}>
              <Select
                mt="md"
                label={t("organizers.language")}
                data={["en", "pt"]}
                {...form.getInputProps("preferredLanguage")}
                w="fit-content"
              />
              <Group>
                <Stack ml="md" align="center">
                  <Text mt="md" size="sm">
                    {t("organizers.is_municipality")}
                  </Text>
                  <Checkbox
                    mt={4}
                    checked={form.values.is_municipality}
                    onChange={(event) =>
                      form.setFieldValue(
                        "is_municipality",
                        event.currentTarget.checked
                      )
                    }
                  />
                </Stack>

                {/* Show enable_weekly checkbox when user is connected */}
                {hasConnectedUser && (
                  <Stack ml="md" align="center">
                    <Text mt="md" size="sm">
                      {t("organizers.enable_weekly")}
                    </Text>
                    <Checkbox
                      mt={4}
                      checked={form.values.enable_weekly}
                      onChange={(event) =>
                        form.setFieldValue(
                          "enable_weekly",
                          event.currentTarget.checked
                        )
                      }
                    />
                  </Stack>
                )}
              </Group>
            </Grid.Col>
          )}

          <Grid.Col
            span={4}
            ml="xs"
            style={{ border: "1px grey solid", borderRadius: 4 }}
          >
            <EditPicture
              defaultValue={organizer?.picture}
              onChange={(f) => {
                setFile(f);
                setImageError(false);
              }}
              width={200}
              height={200}
              isApplicationEdit={isApplication}
              isError={imageError}
            />
            {imageError && (
              <Text
                color="red"
                size="sm"
                mt="xs"
                style={{ textAlign: "center" }}
              >
                You must crop the image first
              </Text>
            )}
          </Grid.Col>
        </Grid>

        <Group mt="xl" mr="xl" justify="space-between" w="100%">
          <Button
            type="submit"
            loading={updateOrganizer.isPending || createOrganizer.isPending}
            size="md"
          >
            {isNew ? t("common.create") : t("common.save")}
          </Button>
          {!isNew && hasConnectedUser && isAdmin && (
            <Tooltip label={connectedUserEmail} position="top" withArrow>
              <Button onClick={handleResetPassword} variant="outline" size="md">
                {t("common.reset_password")}
              </Button>
            </Tooltip>
          )}
        </Group>
      </form>
    </Box>
  );
};
